import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Box,
  Tooltip,
  Divider,
  Alert,
} from '@mui/joy';
import {
  PencilSimple as Edit,
  Trash as Delete,
  CaretDown as ExpandMore,
  CaretUp as ExpandLess,
  Clock as Schedule,
  Lock,
} from '@phosphor-icons/react';
import type { ContactNote } from '../../../types/crm';

interface NoteCardProps {
  note: ContactNote;
  onEdit: (note: ContactNote) => void;
  onDelete: (noteId: number) => void;
}

const priorityColors = {
  low: 'neutral' as const,
  medium: 'primary' as const,
  high: 'warning' as const,
  urgent: 'danger' as const,
};

const categoryLabels = {
  general: 'General',
  call: 'Phone Call',
  meeting: 'Meeting',
  email: 'Email',
  followup: 'Follow-up',
};

const TRUNCATE_LENGTH = 200;

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isLongNote = note.note.length > TRUNCATE_LENGTH;
  const displayContent = isLongNote && !isExpanded 
    ? note.note.substring(0, TRUNCATE_LENGTH) + '...'
    : note.note;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const isFollowupOverdue = note.requires_followup && 
    note.followup_date && 
    !note.followup_completed && 
    new Date(note.followup_date) < new Date();

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar size="sm">
              {note.created_by_name ? getInitials(note.created_by_name) : '?'}
            </Avatar>
            
            <Stack spacing={1} sx={{ flex: 1 }}>
              {/* Author and timestamp */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography level="body-sm" fontWeight="md">
                  {note.created_by_name || 'Unknown User'}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {formatDate(note.created_at)}
                </Typography>
                {note.is_private && (
                  <Tooltip title="Private note">
                    <Lock size={14} color="var(--joy-palette-text-secondary)" />
                  </Tooltip>
                )}
              </Stack>

              {/* Tags */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="sm" variant="soft" color="neutral">
                  {categoryLabels[note.category]}
                </Chip>
                <Chip 
                  size="sm" 
                  variant="soft" 
                  color={priorityColors[note.priority]}
                >
                  {note.priority.toUpperCase()}
                </Chip>
              </Stack>
            </Stack>

            {/* Actions */}
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit note">
                <IconButton 
                  size="sm" 
                  variant="plain"
                  onClick={() => onEdit(note)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete note">
                <IconButton 
                  size="sm" 
                  variant="plain" 
                  color="danger"
                  onClick={() => onDelete(note.id)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Subject */}
          {note.subject && (
            <Typography level="title-sm" sx={{ fontWeight: 'md' }}>
              {note.subject}
            </Typography>
          )}

          {/* Content */}
          <Box>
            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
              {displayContent}
            </Typography>
            
            {isLongNote && (
              <Button
                variant="plain"
                size="sm"
                startDecorator={isExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ mt: 1, p: 0, minHeight: 'auto' }}
              >
                {isExpanded ? 'Show Less' : 'View Entire Note'}
              </Button>
            )}
          </Box>

          {/* Follow-up info */}
          {note.requires_followup && (
            <>
              <Divider />
              <Alert 
                color={isFollowupOverdue ? 'danger' : note.followup_completed ? 'success' : 'warning'}
                variant="soft"
                startDecorator={<Schedule />}
                size="sm"
              >
                <Stack spacing={0.5}>
                  <Typography level="body-xs" fontWeight="md">
                    Follow-up {note.followup_completed ? 'completed' : isFollowupOverdue ? 'overdue' : 'required'}
                  </Typography>
                  {note.followup_date && (
                    <Typography level="body-xs">
                      Due: {formatDate(note.followup_date)}
                    </Typography>
                  )}
                </Stack>
              </Alert>
            </>
          )}

          {/* Attachment indicator */}
          {note.attachment && (
            <Typography level="body-xs" color="primary">
              📎 Attachment: {note.attachment.split('/').pop()}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};